<template>
  <div class="mf-materials-filters">
    <!-- Name Filter -->
    <div class="mf-materials-filter-search">
      <div class="mf-materials-filter-search-input">
        <input
          v-model="filters.text"
          type="text"
          placeholder="Suche"
          @keypress.enter="applyText"
        >
        <Transition name="slide-fade-left">
          <button
            v-if="filters.text?.length > 0"
            class="clear"
            @click="clearText"
          >
            <span>Löschen</span>
          </button>
        </Transition>
      </div>
      <button
        class="mf-materials-filter-search-button"
        @click="applyText"
      >
        <span>Suchen</span>
      </button>
    </div>

    <!-- Specials Filter -->
    <MaterialFiltersEntry
      ref="specialsFilter"
      class="filter-special"
    >
      <template #button>
        <span>{{ specialsString }}</span>
      </template>
      <template #dropdown>
        <div class="mf-materials-filters-multiselect">
          <button
            v-for="special in specials"
            :key="special"
            :class="['mf-materials-filters-multiselect-entry', { active: selectedSpecials.includes(special) }]"
            @click="toggleSpecial(special)"
          >
            {{ special }}
          </button>
        </div>
        <div class="mf-materials-filters-entry-dropdown-actions">
          <button
            v-if="selectedSpecials.length > 0"
            class="btn btn-fourthcolor action-reset"
            @click="clearSpecials"
          >
            Zurücksetzen
          </button>
          <button
            class="btn btn-primary action-accept"
            @click="applySpecials"
          >
            Anwenden
          </button>
        </div>
      </template>
    </MaterialFiltersEntry>

    <!-- Categories Filter -->
    <MaterialFiltersEntry
      ref="categoriesFilter"
      class="filter-category"
    >
      <template #button>
        <span>{{ categoriesString }}</span>
      </template>
      <template #dropdown>
        <div class="mf-materials-filters-multiselect">
          <button
            v-for="category in categories"
            :key="category"
            :class="['mf-materials-filters-multiselect-entry', { active: selectedCategories.includes(category) }]"
            @click="toggleCategory(category)"
          >
            {{ category }}
          </button>
        </div>
        <div class="mf-materials-filters-entry-dropdown-actions">
          <button
            v-if="selectedCategories.length > 0"
            class="btn btn-fourthcolor action-reset"
            @click="clearCategories"
          >
            Zurücksetzen
          </button>
          <button
            class="btn btn-primary action-accept"
            @click="applyCategories"
          >
            Anwenden
          </button>
        </div>
      </template>
    </MaterialFiltersEntry>

    <!-- Subcategories Filter -->
    <MaterialFiltersEntry
      ref="subcategoriesFilter"
      class="filter-subcategory"
    >
      <template #button>
        <span>{{ subcategoriesString }}</span>
      </template>
      <template #dropdown>
        <div class="mf-materials-filters-multiselect">
          <button
            v-for="category in subcategories"
            :key="category"
            :class="[
              'mf-materials-filters-multiselect-entry',
              { active: selectedSubcategories.includes(category) },
            ]"
            @click="toggleSubcategory(category)"
          >
            {{ category }}
          </button>
        </div>
        <div class="mf-materials-filters-entry-dropdown-actions">
          <button
            v-if="selectedSubcategories.length > 0"
            class="btn btn-fourthcolor action-reset"
            @click="clearSubcategories"
          >
            Zurücksetzen
          </button>
          <button
            class="btn btn-primary action-accept"
            @click="applySubcategories"
          >
            Anwenden
          </button>
        </div>
      </template>
    </MaterialFiltersEntry>

    <!-- Reset Filter Button -->
    <div class="mf-materials-filters-reset">
      <button
        class="btn btn-secondary"
        @click="clearFilters"
      >
        <span>Filter zurücksetzen</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import MaterialFiltersEntry from "./MaterialFiltersEntry.vue";
import useMaterialsStore from "@stores/materials";

// store
const materialsStore = useMaterialsStore();

// props & emits
defineProps({
  items: {
    type: Array,
    required: true,
  },
});
const emit = defineEmits(["filter-changed", "filter-reset"]);

// filters
const filters = ref<IFilters>({
  text: "",
  categories: [],
  subcategories: [],
  specials: [],
});
const categories = materialsStore.categories;
const subcategories = materialsStore.subcategories;
const specials = materialsStore.specials;

// name
const applyText = () => {
  applyFilters();
};
const clearText = () => {
  filters.value.text = "";
  applyFilters();
};

// categories
const categoriesFilter = ref<typeof MaterialFiltersEntry>();
const selectedCategories = ref<string[]>([]);
const categoriesString = computed(() => {
  const selectedCategoriesLength = filters.value.categories.length;

  if (selectedCategoriesLength === 0) {
    return "Kategorien";
  }

  if (selectedCategoriesLength === categories.length) {
    return "Alle Kategorien";
  }

  if (selectedCategoriesLength === 1) {
    return "1 Kategorie";
  }

  return `${selectedCategoriesLength} Kategorien`;
});
const toggleCategory = (category: string) => {
  selectedCategories.value = selectedCategories.value.includes(category) ? selectedCategories.value.filter(
    (c) => c !== category
  ) : [...selectedCategories.value, category];
};
const applyCategories = () => {
  categoriesFilter?.value?.hidePopover();
  filters.value.categories = selectedCategories.value;
  applyFilters();
};
const clearCategories = () => {
  categoriesFilter?.value?.hidePopover();
  selectedCategories.value = [];
  applyCategories();
};

// subcategories
const subcategoriesFilter = ref<typeof MaterialFiltersEntry>();
const selectedSubcategories = ref<string[]>([]);
const subcategoriesString = computed(() => {
  const selectedSubcategoriesLength = filters.value.subcategories.length;

  if (selectedSubcategoriesLength === 0) {
    return "Unterkategorien";
  }

  if (selectedSubcategoriesLength === subcategories.length) {
    return "Alle Unterkategorien";
  }

  if (selectedSubcategoriesLength === 1) {
    return "1 Unterkategorie";
  }

  return `${selectedSubcategoriesLength} Unterkategorien`;
});
const toggleSubcategory = (subcategory: string) => {
  selectedSubcategories.value = selectedSubcategories.value.includes(subcategory) ? selectedSubcategories.value.filter(
    (c) => c !== subcategory
  ) : [...selectedSubcategories.value, subcategory];
};
const applySubcategories = () => {
  subcategoriesFilter?.value?.hidePopover();
  filters.value.subcategories = selectedSubcategories.value;
  applyFilters();
};
const clearSubcategories = () => {
  subcategoriesFilter?.value?.hidePopover();
  selectedSubcategories.value = [];
  applySubcategories();
};

// specials
const specialsFilter = ref<typeof MaterialFiltersEntry>();
const selectedSpecials = ref<string[]>([]);
const specialsString = computed(() => {
  const selectedSpecialsLength = filters.value.specials.length;

  if (selectedSpecialsLength === 0) {
    return "Spezialkategorien";
  }

  if (selectedSpecialsLength === specials.length) {
    return "Alle Spezialkategorien";
  }

  if (selectedSpecialsLength === 1) {
    return "1 Spezialkategorie";
  }

  return `${selectedSpecialsLength} Spezialkategorien`;
});
const toggleSpecial = (special: string) => {
  selectedSpecials.value = selectedSpecials.value.includes(special) ? selectedSpecials.value.filter(
    (c) => c !== special
  ) : [...selectedSpecials.value, special];
};
const applySpecials = () => {
  specialsFilter?.value?.hidePopover();
  filters.value.specials = selectedSpecials.value;
  applyFilters();
};
const clearSpecials = () => {
  specialsFilter?.value?.hidePopover();
  selectedSpecials.value = [];
  applySpecials();
};

// methods
const applyFilters = () => {
  emit("filter-changed", filters.value);
};
const clearFilters = () => {
  clearText();
  clearCategories();
  clearSubcategories();
  clearSpecials();
  emit("filter-reset");
};
</script>

<style>
/* stylelint-disable-next-line selector-class-pattern */
.dp__theme_light {
  --dp-text-color: #3b343c;
  --dp-primary-color: #cf6244;
  --dp-primary-disabled-color: #3d1c41;
  --dp-primary-text-color: #ffffff;
  --dp-secondary-color: #b6b6b6;
}

.mf-materials-filters-slider {
  --slider-connect-bg: #cf6244;
  --slider-tooltip-bg: #cf6244;
}
</style>
